import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Page atteinte depuis le lien de désinscription des emails. Le RGPD veut que
// retirer son consentement soit aussi simple que de le donner : la
// désinscription se fait donc dès l'arrivée, sans demander de confirmation
// ni d'identification.
export default function Desinscription() {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [etat, setEtat] = useState('encours'); // encours | fait | echec
  const jeton = searchParams.get('token');
  const fr = language === 'fr';

  useEffect(() => {
    if (!jeton) {
      setEtat('echec');
      return;
    }
    let annule = false;
    axios
      .post(`${API}/newsletter/unsubscribe`, { token: jeton })
      .then(() => { if (!annule) setEtat('fait'); })
      .catch(() => { if (!annule) setEtat('echec'); });
    return () => { annule = true; };
  }, [jeton]);

  return (
    <div className="min-h-screen pt-20 lg:pt-24 pb-16 flex items-center justify-center" data-testid="unsubscribe-page">
      <SEO
        title={fr ? 'Désinscription - AfriOne BD' : 'Unsubscribe - AfriOne BD'}
        description={fr
          ? 'Désinscription de la newsletter AfriOne BD.'
          : 'Unsubscribe from the AfriOne BD newsletter.'}
        canonical="https://afrionebd.eu/desinscription"
        noindex
      />

      <div className="max-w-md mx-auto px-4 text-center">
        {etat === 'encours' && (
          <>
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
            <p className="text-text_secondary">
              {fr ? 'Désinscription en cours…' : 'Unsubscribing…'}
            </p>
          </>
        )}

        {etat === 'fait' && (
          <>
            <Check className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h1 className="text-xl sm:text-2xl font-bold font-heading text-text_primary mb-3">
              {fr ? 'Vous êtes désinscrit' : 'You are unsubscribed'}
            </h1>
            <p className="text-sm sm:text-base text-text_secondary mb-6">
              {fr
                ? "Vous ne recevrez plus nos emails. Vous pouvez vous réinscrire à tout moment depuis la page d'accueil."
                : 'You will no longer receive our emails. You can subscribe again at any time from the home page.'}
            </p>
          </>
        )}

        {etat === 'echec' && (
          <>
            <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h1 className="text-xl sm:text-2xl font-bold font-heading text-text_primary mb-3">
              {fr ? 'Lien invalide' : 'Invalid link'}
            </h1>
            <p className="text-sm sm:text-base text-text_secondary mb-6">
              {fr
                ? "Ce lien de désinscription n'est pas reconnu. Il a peut-être déjà été utilisé. Écrivez-nous et nous retirerons votre adresse manuellement."
                : 'This unsubscribe link was not recognised. It may already have been used. Write to us and we will remove your address manually.'}
            </p>
            <a
              href="mailto:contact@afrionebd.eu"
              className="text-primary hover:underline text-sm"
            >
              contact@afrionebd.eu
            </a>
          </>
        )}

        {etat !== 'encours' && (
          <div className="mt-6">
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-primary text-white font-medium hover:bg-primary_hover transition-colors"
            >
              {fr ? "Retour à l'accueil" : 'Back to home'}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
